import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { pubsub } from "@assets/js/imaios-global/imaios-global";

const TTL_HISTORIC_SEARCH_ITEM = 60 * 60 * 24 * 7;
const HISTORIC_MAX_LENGTH = 6;
const LOCAL_STORAGE_HISTORIC = "tree-view-search-historic";

const siteAccess = window.imaios.getSiteAccess();

const DEFAULT_EMPTY_HISTORIC = {};

export const useHistoricStore = defineStore("historic", () => {
  const historic = ref(DEFAULT_EMPTY_HISTORIC);

  const getHistoricBySiteAccessAndTaId = (taId) =>
    computed(() => {
      const historicItemsBySiteAccessAndTaId =
        historic.value[siteAccess]?.filter((i) => i.value.taId === taId) ?? [];
      const sortedHistoricItemsBySiteAccessAndTaId =
        historicItemsBySiteAccessAndTaId.sort((itemA, itemB) => itemB.visitTime - itemA.visitTime);

      return sortedHistoricItemsBySiteAccessAndTaId.map((item) => item.value);
    });

  /**
   * @param application
   * @example 'eanatomy' || 'vetanatomy'
   */
  const getHistoricByApplication = (application) =>
    computed(() => {
      return historic.value[siteAccess]?.filter(
          (historicItem) => historicItem.value.application === application
        ) ?? [];
    });

  /**
   * @param application
   * @example 'eanatomy' || 'vetanatomy'
   */
  const getSortedHistoricByApplication = (application) =>
      computed(() => {
        const sortedHistoricItemsByApplication =
            getHistoricByApplication(application).value.sort(
                (itemA, itemB) => itemB.visitTime - itemA.visitTime
            );

        return sortedHistoricItemsByApplication.map((item) => item.value);
      })

  function initializeStore() {
    let localStorageHistoric;

    try {
      localStorageHistoric = localStorage.getItem(LOCAL_STORAGE_HISTORIC);
    } catch (e) {
      // localStorage unavailable, initialize store from it
    }

    if (localStorageHistoric) {
      let parsedLocalStorageHistoric;
      try {
        parsedLocalStorageHistoric = JSON.parse(localStorageHistoric);
      } catch (e) {
        if (e instanceof SyntaxError) {
          // local storage historic has been corrupted and is not readable, reset it
          localStorage.setItem(
            LOCAL_STORAGE_HISTORIC,
            JSON.stringify(DEFAULT_EMPTY_HISTORIC)
          );
          parsedLocalStorageHistoric = DEFAULT_EMPTY_HISTORIC;
        } else {
          throw e;
        }
      }
      Object.keys(parsedLocalStorageHistoric).forEach((siteAccess) =>
        parsedLocalStorageHistoric[siteAccess].filter(
          (historicItem) =>
            historicItem.visitTime + TTL_HISTORIC_SEARCH_ITEM >=
            new Date().getTime()
        )
      );

      historic.value = parsedLocalStorageHistoric;
    }
  }

  function visitItem(visitedItem) {
    pubsub.publish("closeSearchDropdown"); // close dropdown each time visite an item by click or navigate

    const historicOfSiteAccess = historic.value[siteAccess] ?? [];

    const itemIndex = historicOfSiteAccess.findIndex(
      (historicItem) => historicItem.value.hash === visitedItem.hash
    );

    if (itemIndex >= 0) {
      //refresh item in historic
      historic.value[siteAccess][itemIndex].visitTime = new Date().getTime();
      if (visitedItem.parentList) historic.value[siteAccess][itemIndex].value = visitedItem;
    } else {
      const sortedHistoricByApplication = getSortedHistoricByApplication(visitedItem.application).value;

      if (sortedHistoricByApplication.length === HISTORIC_MAX_LENGTH) {
        //remove oldest item by application
        const oldestItem = sortedHistoricByApplication.at(-1);
        historic.value[siteAccess] = historicOfSiteAccess.filter(
            (item) => item.value.hash !== oldestItem.hash
        );
      }

      //add item to historic
      (historic.value[siteAccess] ??= []).push({
        value: visitedItem,
        visitTime: new Date().getTime(),
      });
    }
  }
  return {
    historic,
    getHistoricByApplication,
    getSortedHistoricByApplication,
    getHistoricBySiteAccessAndTaId,
    initializeStore,
    visitItem,
  };
});

export const writeHistoricToLocaleStorage = (historic) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_HISTORIC, JSON.stringify(historic));
  } catch (e) {
    // localStorage unavailable, won't store in it
  }
};
